import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Bookmark, Share2, Globe, Lock, Trash2, Compass, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PlanetOrb } from "./PlanetOrb";
import { RarityBadge, BiomeBadge, HazardMeter } from "./Badges";
import { PlanetTabs } from "./PlanetTabs";
import { Reactions } from "./Reactions";
import { Comments } from "./Comments";

export const PlanetDossier = ({ planet, setPlanet, reveal = false, onDiscoverAgain, onNeedAuth }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const guard = (fn) => async () => {
    if (!user) return onNeedAuth ? onNeedAuth() : toast("Sign in to continue");
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const toggleSave = guard(async () => {
    const { data } = await api.post(`/planets/${planet.id}/save`);
    setPlanet({ ...planet, saved: data.saved });
    toast.success(data.saved ? `${planet.name} saved to your library` : `${planet.name} removed from library`);
  });

  const share = guard(async () => {
    const { data } = await api.post(`/planets/${planet.id}/share`);
    const url = `${window.location.origin}${data.share_path}`;
    try { await navigator.clipboard.writeText(url); toast.success("Share link copied to clipboard"); }
    catch { toast.success(`Share link: ${url}`); }
    setPlanet({ ...planet, share_token: data.share_token, is_shared: true });
  });

  const toggleVisibility = guard(async () => {
    const { data } = await api.patch(`/planets/${planet.id}/visibility`, { is_public: !planet.is_public });
    setPlanet({ ...planet, is_public: data.is_public });
    toast.success(data.is_public ? "Broadcasting to the public signal feed" : "World sealed from the public feed");
  });

  const purge = guard(async () => {
    if (!window.confirm(`Purge ${planet.name} from the archive? This cannot be undone.`)) return;
    await api.delete(`/planets/${planet.id}`);
    toast.success("Record purged");
    navigate("/universe");
  });

  const regenerate = guard(async () => {
    if (!window.confirm("Discard this world and launch a fresh probe?")) return;
    await api.delete(`/planets/${planet.id}`);
    onDiscoverAgain();
  });

  return (
    <div className="space-y-8" data-testid="planet-dossier">
      <div className="grid lg:grid-cols-[minmax(280px,440px)_1fr] gap-10 items-center">
        <motion.div initial={reveal ? { opacity: 0, scale: 0.6, filter: "blur(18px)" } : false}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="animate-float">
          <PlanetOrb planet={planet} />
        </motion.div>

        <motion.div initial={reveal ? { opacity: 0, x: 24 } : false} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35, duration: 0.7 }}>
          <div className="flex items-center gap-3 mb-3">
            <span className="font-mono text-xs tracking-[0.3em] text-cyan-400 uppercase" data-testid="planet-star-code">{planet.star_code}</span>
            {planet.source === "template" && <span className="label-mono !text-[#FFC700]" data-testid="planet-template-badge">Provisional survey</span>}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-none text-slate-50 text-glow" data-testid="planet-name">
            {planet.name}
          </h1>
          <p className="mt-4 text-base md:text-lg text-slate-300 max-w-xl" data-testid="planet-tagline">{planet.tagline}</p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <BiomeBadge biome={planet.biome} />
            <RarityBadge rarity={planet.rarity} />
            <HazardMeter level={planet.hazard_level} />
          </div>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
            {[["Atmosphere", planet.atmosphere], ["Gravity", planet.gravity], ["Temp", planet.temperature], ["Day", planet.day_length]].map(([k, v]) => (
              <div key={k}><p className="label-mono">{k}</p><p className="font-mono text-xs text-slate-200 mt-1">{v}</p></div>
            ))}
          </div>

          <p className="label-mono mt-6">Discovered by {planet.owner_name} · {new Date(planet.created_at).toLocaleDateString()}</p>

          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={toggleSave} disabled={busy} className={`btn-ghost ${planet.saved ? "active" : ""}`} data-testid="save-planet-button">
              <Bookmark className={`h-4 w-4 ${planet.saved ? "fill-cyan-300" : ""}`} /> {planet.saved ? "Saved" : "Save"}
            </button>
            {planet.is_owner && (
              <>
                <button onClick={share} disabled={busy} className="btn-ghost" data-testid="share-planet-button">
                  <Share2 className="h-4 w-4" /> {planet.is_shared ? "Copy share link" : "Share by link"}
                </button>
                <button onClick={toggleVisibility} disabled={busy} className={`btn-ghost ${planet.is_public ? "active" : ""}`} data-testid="publish-toggle-button">
                  {planet.is_public ? <Globe className="h-4 w-4" /> : <Lock className="h-4 w-4" />} {planet.is_public ? "Public" : "Private"}
                </button>
                {onDiscoverAgain && (
                  <button onClick={regenerate} disabled={busy} className="btn-ghost" data-testid="regenerate-planet-button" title="Discard and re-probe">
                    <RefreshCw className="h-4 w-4" /> Regenerate
                  </button>
                )}
                <button onClick={purge} disabled={busy} className="btn-ghost !border-[#FF5500]/40 hover:!bg-[#FF5500]/15" data-testid="delete-planet-button" title="Purge record">
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            )}
            {onDiscoverAgain && (
              <button onClick={onDiscoverAgain} className="btn-primary" data-testid="discover-another-button">
                <Compass className="h-4 w-4" /> Discover another
              </button>
            )}
          </div>
        </motion.div>
      </div>

      <motion.div initial={reveal ? { opacity: 0, y: 24 } : false} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }} className="space-y-8">
        <PlanetTabs planet={planet} />
        <div className="glass rounded-xl p-6 sm:p-8">
          <p className="label-mono mb-4">Explorer reactions</p>
          <Reactions planet={planet} onChange={setPlanet} onNeedAuth={onNeedAuth} />
        </div>
        <Comments planetId={planet.id} onNeedAuth={onNeedAuth} />
      </motion.div>
    </div>
  );
};
